import { useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';

import { AutoExposureIcon } from '@dbt-labs/dbt-dag';
import { HealthIssue } from '@dbt-labs/metadata-shared';
import { CellContext, Link } from '@dbt-labs/sourdough';

import {
  AutoExposureBiProvider,
  ExposureDefinitionType,
  ExposureFilter,
} from '@/__generated__/graphql';
import { DismissableNotificationBanner } from '@/components/DismissableNotificationBanner';
import {
  AUTO_BI_PROVIDER_FILTER_PARAM,
  AutoBiProviderFilterDropdown,
} from '@/components/ExposureFilters/AutoBiProviderFilterDropdown';
import {
  EXPOSURE_MODE_PARAM,
  ExposureModeDropdown,
} from '@/components/ExposureFilters/ExposureModeDropdown';
import {
  OWNER_PARAM,
  OwnerFilterDropdown,
} from '@/components/ExposureFilters/OwnerFilterDropdown';
import { PageHeading } from '@/components/PageHeading';
import { ExposureTrustSignalCell } from '@/components/Table/ExposureTrustSignalCell';
import { getNameLinkCell } from '@/components/Table/LinkCell';
import { TruncatedCell } from '@/components/TruncatedCell';
import { useDbtCloudProject } from '@/context/DbtCloudProjectProvider';
import { useDbtCloudAccount } from '@/context/DbtCloudProvider';
import { useFeatureFlag } from '@/context/FeatureFlagProvider';
import { useIntegrations } from '@/hooks/admin/integration';
import {
  AppliedExposureListNode,
  useDiscoveryAppliedExposures,
} from '@/hooks/discovery/appliedExposures';
import { useLinkToDbtCloud, useLinkToExplorer } from '@/hooks/links';
import { useUrlProjectParams } from '@/hooks/params';
import { useEnvironmentScope } from '@/hooks/useEnvironmentScope';
import { formatBiProviderName } from '@/util/dbt/exposure';
import { INITIAL_PAGE_SIZE, ResourceFilterTable } from '../ResourceFilterTable';

type TableData = {
  uniqueId: string;
  name: string;
  owner: string | null | undefined;
  ownerEmail: string | null | undefined;
  type: string | null | undefined;
  maturity: string | null | undefined;
  integration: AutoExposureBiProvider | null;
  exposureMode: 'Auto' | 'Manual';
  healthIssues: HealthIssue[];
};

const getTableData = (exposure: AppliedExposureListNode): TableData => {
  return {
    name: exposure.name!,
    uniqueId: exposure.uniqueId,
    owner: exposure.ownerName,
    ownerEmail: exposure.ownerEmail,
    maturity: exposure.maturity,
    type: exposure.exposureType,
    integration: exposure.autoBiProvider,
    exposureMode: exposure.autoBiProvider ? 'Auto' : 'Manual',
    healthIssues: exposure.healthIssues,
  };
};

export const ExposureFilterView = () => {
  const pageSize = INITIAL_PAGE_SIZE;
  const { hasAccountFeature } = useDbtCloudAccount();
  const { accountId, projectId, environmentId, userHasPermissions } =
    useDbtCloudProject();
  const projectScope = useUrlProjectParams();
  const getDetailsPageLink = useLinkToExplorer('resourceDetails');
  const isPowerBIAutoExposuresEnabled = useFeatureFlag(
    'meta4178PowerBiautoExposures',
    false,
  );

  const AUTOEXPOSURE_BANNER_LOCAL_STORAGE_KEY = `dbt-cloud.explorer.autoexposure-banner-${projectId}`;

  const autoExposuresEnabled = hasAccountFeature('explorerAutoExposures');

  const integrationsQuery = useIntegrations({
    accountId,
    projectId,
    enabled: autoExposuresEnabled,
  });
  const hasLineageIntegration = integrationsQuery.data?.some((i) =>
    ['tableau', 'powerbi'].includes(i.name),
  );
  const hasPermissionToWriteIntegration = userHasPermissions('integrations_write');
  const hasPermissionToReadIntegration = userHasPermissions('integrations_read');
  const showIntegrationBanner =
    autoExposuresEnabled &&
    integrationsQuery.isFetched &&
    !hasLineageIntegration &&
    hasPermissionToReadIntegration;

  const lineageIntegrationSettings = useLinkToDbtCloud('lineageIntegrationSettings');

  const { userId } = useEnvironmentScope();

  const [searchParams] = useSearchParams();

  const filter = useMemo((): Partial<ExposureFilter> => {
    return {
      ownerName: searchParams.get(OWNER_PARAM),
      autoBiProvider: autoExposuresEnabled
        ? (searchParams.get(AUTO_BI_PROVIDER_FILTER_PARAM) as
            | AutoExposureBiProvider
            | undefined)
        : undefined,
      definitionType: autoExposuresEnabled
        ? (searchParams.get(EXPOSURE_MODE_PARAM) as ExposureDefinitionType | undefined)
        : ExposureDefinitionType.Manual,
    };
  }, [autoExposuresEnabled, searchParams]);

  const paginatedSourceQuery = useDiscoveryAppliedExposures({
    environmentId,
    userId,
    filter,
    first: pageSize,
  });

  const sources = paginatedSourceQuery.data;
  const pageInfo = paginatedSourceQuery.pageInfo;

  const data = useMemo((): any[] | undefined => {
    return sources?.map(getTableData);
  }, [sources]);

  if (paginatedSourceQuery.error) return <>{paginatedSourceQuery.error.stack}</>;
  const isLoadingMore = paginatedSourceQuery.fetching;
  const isInitialLoad =
    paginatedSourceQuery.fetching && paginatedSourceQuery.data === undefined;

  return (
    <>
      <PageHeading>Exposures</PageHeading>
      {showIntegrationBanner && (
        <DismissableNotificationBanner
          className="my-4"
          isGlobal={false}
          localStorageKey={AUTOEXPOSURE_BANNER_LOCAL_STORAGE_KEY}
          notification={{
            id: 'configureAutoExposures',
            type: 'info',
            header: 'Configure auto exposures',
            helperText: (
              <p>
                If you use Tableau{' '}
                {isPowerBIAutoExposuresEnabled ? 'or Power BI ' : ' '}
                as your BI tool, you can import dashboards into your dbt Cloud project
                as exposures to view their details and lineage in Explorer.{' '}
                {hasPermissionToWriteIntegration && (
                  <span>
                    Set up an integration in your{' '}
                    <Link
                      isInternal={false}
                      shouldOpenNewTab
                      to={
                        lineageIntegrationSettings?.({
                          accountId,
                          projectId,
                        }) || ''
                      }
                    >
                      project settings
                    </Link>{' '}
                    and learn more{' '}
                    <Link
                      isInternal={false}
                      shouldOpenNewTab
                      to="https://docs.getdbt.com/docs/cloud-integrations/downstream-exposures"
                    >
                      here
                    </Link>
                    .
                  </span>
                )}
                {!hasPermissionToWriteIntegration && (
                  <span>
                    Learn more{' '}
                    <Link
                      isInternal={false}
                      shouldOpenNewTab
                      to="https://docs.getdbt.com/docs/cloud-integrations/downstream-exposures"
                    >
                      here
                    </Link>{' '}
                    and ask your admin for assistance setting up an integration in this
                    project's settings.
                  </span>
                )}
              </p>
            ),
          }}
        />
      )}
      <div className="my-2 flex flex-wrap gap-2">
        <OwnerFilterDropdown />
        {autoExposuresEnabled && <AutoBiProviderFilterDropdown />}
        {autoExposuresEnabled && <ExposureModeDropdown />}
      </div>
      <ResourceFilterTable
        pluralizedResultName="exposures"
        data={data}
        columns={[
          {
            header: 'Name',
            accessorKey: 'name',
            id: 'name',
            cell: getNameLinkCell(getDetailsPageLink, projectScope),
          },
          {
            header: 'Health',
            accessorKey: 'healthIssues',
            cell: ExposureTrustSignalCell,
            size: 90,
            minSize: 90,
            enableSorting: false,
          },
          {
            header: 'Type',
            accessorKey: 'type',
            size: 100,
            cell: TruncatedCell,
          },
          {
            header: 'Owner',
            accessorKey: 'owner',
            cell: TruncatedCell,
          },
          {
            header: 'Owner email',
            accessorKey: 'ownerEmail',
            cell: TruncatedCell,
          },
          ...(autoExposuresEnabled
            ? [
                {
                  header: 'Integration',
                  size: 100,
                  cell: (props: CellContext<TableData, any>) => {
                    const data: TableData = props.row.original;
                    const { integration } = data;
                    return (
                      <div className="flex flex-row">
                        {!!integration && (
                          <AutoExposureIcon
                            biProvider={integration}
                            className="h-5 pr-1.5"
                          />
                        )}
                        <span>{formatBiProviderName(integration)}</span>
                      </div>
                    );
                  },
                },
              ]
            : []),
          ...(autoExposuresEnabled
            ? [
                {
                  header: 'Exposure Mode',
                  accessorKey: 'exposureMode',
                  size: 100,
                  cell: TruncatedCell,
                },
              ]
            : []),
        ]}
        pageInfo={pageInfo}
        isLoading={isInitialLoad}
        isLoadingMore={isLoadingMore}
        pageSize={pageSize}
        fetchMore={paginatedSourceQuery.fetchMore}
      />
    </>
  );
};
